import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Trash2, Globe, Check, User } from 'lucide-react';
import { getJWTFromLocalStorage } from '../services/helpers';

const SERVER_URL = `http${process.env.REACT_APP_API_URL}`;

// Platform Icon component with enhanced styling
const PlatformIcon = ({ platform }) => {
  const getIconClass = (p) => {
    if (!p) return 'default';
    const lower = p.toLowerCase();
    if (['discord', 'snapchat', 'instagram', 'tiktok', 'reddit'].includes(lower)) return lower;
    return 'default';
  };

  const getIconElement = (p) => {
    if (!p) return <Globe size={16} />;
    switch (p.toLowerCase()) {
      case 'discord': return <i className="fa-brands fa-discord"></i>;
      case 'snapchat': return <i className="fa-brands fa-snapchat"></i>;
      case 'instagram': return <i className="fa-brands fa-instagram"></i>;
      case 'tiktok': return <i className="fa-brands fa-tiktok"></i>;
      case 'reddit': return <i className="fa-brands fa-reddit-alien"></i>;
      default: return <Globe size={16} />;
    }
  };

  return (
    <div className={`platform-icon-modern ${getIconClass(platform)}`}>
      {getIconElement(platform)}
    </div>
  );
};

const WardCard = ({ ward, onRemove }) => {
  const [platforms, setPlatforms] = useState(ward.platforms || []);
  const [isAddingPlatform, setIsAddingPlatform] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const availablePlatforms = ['Discord', 'Snapchat', 'Instagram', 'TikTok', 'Reddit'];

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(part => part[0]).join("").toUpperCase().substring(0, 2);
  };

  const getColorFromString = (str) => {
    const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#14b8a6", "#f59e0b"];
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const handleAddPlatform = async () => {
    if (!selectedPlatform) return;
    setIsLoading(true);
    try {
      const token = await getJWTFromLocalStorage();
      const response = await fetch(`${SERVER_URL}/link-ward-platform`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ward_id: ward.id, platform_name: selectedPlatform })
      });

      if (!response.ok) throw new Error('Failed to add platform');
      const data = await response.json();
      setPlatforms([...platforms, data]);
      setIsAddingPlatform(false);
      setSelectedPlatform('');
      setError('');
    } catch (err) {
      setError('Failed to add platform');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePlatform = async (platformName) => {
    if (!window.confirm(`Remove ${platformName}?`)) return;
    try {
      const token = await getJWTFromLocalStorage();
      const response = await fetch(`${SERVER_URL}/unlink-ward-platform`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ward_id: ward.id, platform_name: platformName })
      });
      if (response.ok) {
        setPlatforms(platforms.filter(p => p.platform_name !== platformName));
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="ward-card-modern">
      <div className="ward-card-header">
        <div className="ward-identity">
          <div className="ward-avatar-modern" style={{ background: getColorFromString(ward.username || "") }}>
            {getInitials(ward.full_name || ward.username)}
          </div>
          <div className="ward-text">
            <h3>{ward.full_name || ward.username}</h3>
            <span className="ward-username">@{ward.username}</span>
          </div>
        </div>
        <button className="icon-btn-danger" onClick={onRemove} title="Remove Ward">
          <Trash2 size={18} />
        </button>
      </div>

      <div className="ward-platforms-section">
        <div className="section-header">
          <span className="section-title">Values & Platforms</span>
        </div>

        <div className="platforms-grid">
          {platforms.map(p => (
            <div key={p.id || p.platform_name} className="platform-pill">
              <PlatformIcon platform={p.platform_name} />
              <span>{p.platform_name}</span>
              <button
                className="remove-platform-mini"
                onClick={() => handleRemovePlatform(p.platform_name)}
              >
                <X size={12} />
              </button>
            </div>
          ))}

          {isAddingPlatform ? (
            <div className="add-platform-inline">
              <select
                autoFocus
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="platform-select-mini"
              >
                <option value="">Select...</option>
                {availablePlatforms.filter(p => !platforms.find(ep => ep.platform_name === p)).map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <button className="confirm-btn-mini" onClick={handleAddPlatform} disabled={!selectedPlatform || isLoading}>
                <Check size={14} />
              </button>
              <button className="cancel-btn-mini" onClick={() => setIsAddingPlatform(false)}>
                <X size={14} />
              </button>
            </div>
          ) : (
            <button className="add-platform-pill" onClick={() => setIsAddingPlatform(true)}>
              <Plus size={14} />
              <span>Add Platform</span>
            </button>
          )}
        </div>
        {error && <div className="error-text-mini">{error}</div>}
      </div>
    </div>
  );
};

const Wards = () => {
  const [wards, setWards] = useState([]);
  const [newWardName, setNewWardName] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const fetchWards = async () => {
    try {
      const token = await getJWTFromLocalStorage();
      if (!token) return navigate('/login');

      const res = await fetch(`${SERVER_URL}/wards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) return navigate('/login');
      const data = await res.json();
      setWards(data || []);
      console.log("Fetched wards:", data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchWards(); }, []);

  const handleAddWard = async (e) => {
    e.preventDefault();
    if (!newWardName.trim()) return;

    setIsAdding(true);
    try {
      const token = await getJWTFromLocalStorage();
      const res = await fetch(`${SERVER_URL}/link-guardian-ward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ward_username: newWardName.trim() })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed");
      }

      const data = await res.json();
      setWards([...wards, data]);
      setNewWardName("");
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveWard = async (username) => {
    if (!window.confirm("Are you sure you want to remove this ward?")) return;
    try {
      const token = await getJWTFromLocalStorage();
      await fetch(`${SERVER_URL}/unlink-guardian-ward`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ward_username: username })
      });
      setWards(wards.filter(w => w.username !== username));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="content-container">
      <div className="modern-header">
        <div>
          <h1 className="page-title">Wards Configuration</h1>
          <p className="page-subtitle">Manage your protected accounts and platform links.</p>
        </div>
      </div>

      <div className="add-ward-bar">
        <div className="input-group-modern">
          <input
            type="text"
            placeholder="Link a new ward by username..."
            value={newWardName}
            onChange={(e) => setNewWardName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddWard(e)}
          />
          <button
            className="btn-primary-modern"
            onClick={handleAddWard}
            disabled={isAdding || !newWardName.trim()}
          >
            {isAdding ? 'Linking...' : 'Link Ward'}
          </button>
        </div>
        {error && <div className="error-message-inline">{error}</div>}
      </div>

      <div className="wards-grid-modern">
        {wards.map(ward => (
          <WardCard key={ward.id} ward={ward} onRemove={() => handleRemoveWard(ward.username)} />
        ))}
        {wards.length === 0 && (
          <div className="empty-state-card">
            <div className="empty-icon-circle">
              <User size={32} />
            </div>
            <h3>No Wards Linked</h3>
            <p>Start by entering a username above to link your first ward.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wards;